import { store } from 'store/store';
import { isNackNegotiated } from 'utils/kurentoNack';
import {
    NACK_STATS_NO_NACK_OBSERVATION_MS,
    NACK_STATS_WINDOW_MS,
    formatNackStatsLine,
    isNackStatsReset,
    isSignificantNack,
    pickNackStats,
    summarizeNackStatsWindow,
} from 'utils/webRtcNackStats';

const ENABLED_ENVS = ['develop', 'staging', 'production'];

// BEAST-13647. Logs at most two [WEBRTC NACK STATS] lines per AIQ site per tab, for Kurento connections:
//  - trigger="nack": the first stats window with significant NACK on any camera of the site. The first window of
//    every connection is skipped: stream start-up NACKs would otherwise use up the site's single line
//  - trigger="no_nack": the site's cameras were observed for NACK_STATS_NO_NACK_OBSERVATION_MS in total (summed over
//    its connections, so observedStreamMin is stream-minutes, not wall-clock minutes) without significant NACK
// nackNegotiated shows whether Kurento's SDP answer enables NACK, so disabling it per site can be verified.
// deskId and site are what the kurentoNackConfigEnabled flag config targets (deskIds.<deskId>.locationNames, see
// utils/kurentoNack.js). The per-camera decision is logged by CameraStreamConnection as [WEBRTC NACK CONFIG]
// (why NACK is disabled or kept) and "generated offer without NACK" (NACK really disabled on that connection).
// It only reads the inbound-rtp stats FreezeDetector already collects, so it adds no getStats() calls.
// Nothing is sent to the streaming-observability service: console.log is forwarded to AIQ /logs (Grafana) by aiqLogger.
export class NackStatsLogger {
    #enabled;
    #log;
    #getContext;
    #windowByPeerConnection = new WeakMap(); // peerConnection -> { start, warmedUp }
    #siteStates = new Map(); // site -> { nackLogged, noNackLogged, observedMs }

    constructor({
        enabled = ENABLED_ENVS.includes(import.meta.env.VITE_ENV),
        log = (line) => console.log(line),
        getContext = currentUserContext,
    } = {}) {
        this.#enabled = enabled;
        this.#log = log;
        this.#getContext = getContext;
    }

    observe(source, peerConnection, inboundRtp) {
        if (!this.#enabled || !peerConnection || !inboundRtp) return;
        try {
            this.#observe(source, peerConnection, pickNackStats(inboundRtp));
        } catch {
            // diagnostics must never break freeze detection
        }
    }

    #observe(source, peerConnection, sample) {
        const site = source?.getSiteName?.() || '';
        const siteState = this.#getSiteState(site);
        if (siteState.nackLogged) return; // nothing left to report for this site

        const statsWindow = this.#windowByPeerConnection.get(peerConnection);
        if (!statsWindow || isNackStatsReset(statsWindow.start, sample)) {
            this.#windowByPeerConnection.set(peerConnection, { start: sample, warmedUp: false });
            return;
        }
        if (sample.timestamp - statsWindow.start.timestamp < NACK_STATS_WINDOW_MS) return;

        const windowStart = statsWindow.start;
        statsWindow.start = sample;
        if (!statsWindow.warmedUp) {
            statsWindow.warmedUp = true;
            return;
        }

        const summary = summarizeNackStatsWindow(windowStart, sample);
        if (!summary) return;

        const nackNegotiated = isNackNegotiated(peerConnection.remoteDescription?.sdp);

        if (isSignificantNack(summary)) {
            siteState.nackLogged = true;
            this.#log(
                formatNackStatsLine('nack', {
                    site,
                    ...this.#getContext(),
                    kurentoId: source?.kurentoId,
                    cameraId: source?.cameraId,
                    streamType: source?.activeStreamType,
                    nackNegotiated,
                    ...summary,
                })
            );
            return;
        }

        siteState.observedMs += summary.windowSec * 1000;
        if (!siteState.noNackLogged && siteState.observedMs >= NACK_STATS_NO_NACK_OBSERVATION_MS) {
            siteState.noNackLogged = true;
            this.#log(
                formatNackStatsLine('no_nack', {
                    site,
                    ...this.#getContext(),
                    nackNegotiated,
                    observedStreamMin: Math.round(siteState.observedMs / 60000),
                })
            );
        }
    }

    #getSiteState(site) {
        let siteState = this.#siteStates.get(site);
        if (!siteState) {
            siteState = { nackLogged: false, noNackLogged: false, observedMs: 0 };
            this.#siteStates.set(site, siteState);
        }
        return siteState;
    }
}

function currentUserContext() {
    const state = store?.getState?.();
    return {
        username: state?.auth?.user?.username,
        desk: state?.desks?.currentDesk?.name,
        deskId: state?.desks?.currentDesk?.id,
    };
}

export const nackStatsLogger = new NackStatsLogger();
